import { ChangeDetectionStrategy, Component, computed, ElementRef, input, viewChild } from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faClock } from '@fortawesome/free-solid-svg-icons';
import { SignalStringFieldBaseDirective } from '../signal-field-base.directive';

/**
 * Low-level time input for signal forms, the counterpart to `DateFieldComponent`.
 *
 * Everything store- and validation-related sits in `SignalStringFieldBaseDirective`; this
 * class adds only the native picker bounds, the seconds toggle and the clock icon:
 *
 * ```html
 * <pace-core-time-field [formField]="signalForm.timeValue" ariaLabel="Start time" />
 * ```
 *
 * The value is the HTML5 time string the native input works with — `HH:mm`, or
 * `HH:mm:ss` when `displaySeconds` is set (empty when unset) — rather than a
 * `moment`, which keeps any form model built from these fields directly
 * serializable into an NgRx slice.
 *
 * The field is keyboard-first, exactly like `DateFieldComponent`: the native input's
 * `hh`/`mm` segments take typed digits, so nothing here may steal a click aimed at one
 * of them. The picker has the same two triggers — the clock adornment for the mouse,
 * and `Alt`+`ArrowDown` or `F4` for the keyboard.
 *
 * Unlike the reactive-forms `TimeInputComponent` this does not implement the
 * `ConfigInterface.getUseCustomTimeInput()` variant, which swaps the native
 * picker for a text input plus the `TimeManualInputComponent` modal. Adding it
 * would mean a second template branch here and 12h/24h conversion on the way in
 * and out of the model; it is not needed to evaluate the store wiring.
 */
@Component({
    selector: 'pace-time-field',
    templateUrl: './time-field.component.html',
    styleUrls: ['./time-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FaIconComponent],
})
export class TimeFieldComponent extends SignalStringFieldBaseDirective {
    /** HTML5 time the native picker is limited to; empty for no limit */
    public readonly minTime = input('');
    public readonly maxTime = input('');

    /** lets the native picker edit seconds, and widens the value to `HH:mm:ss` */
    public readonly displaySeconds = input(false);

    /** `step` in seconds; 60 keeps the native picker on whole minutes */
    public readonly step = computed(() => (this.displaySeconds() ? 1 : 60));

    public readonly faClock = faClock;

    private readonly timeInput = viewChild.required<ElementRef<HTMLInputElement>>('timeInput');

    public openPicker(): void {
        if (!this.editable()) {
            return;
        }

        // focus first so the caret is back in the field once the picker closes
        this.timeInput().nativeElement.focus();
        this.timeInput().nativeElement.showPicker();
    }

    /**
     * The conventional "open the dropdown" keys, which the field has to honour itself
     * because the native picker indicator is hidden. A bare `ArrowDown` is left to the
     * input, which uses it to decrement the focused segment.
     */
    public onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'F4' || (event.altKey && event.key === 'ArrowDown')) {
            event.preventDefault();
            this.openPicker();
        }
    }
}
